import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/contexts/AuthContext';
import { DealCard } from '@/components/DealCard';
import { SkeletonCard, SkeletonText } from '@/components/Skeleton';
import { listSavedDeals } from '@/lib/db/buyer/saved-deals';
import { listBuyerRequests, type BuyerRequestRowJoined } from '@/lib/db/buyer/requests';
import { mapSavedRowToDealView } from './DashboardSavedPage.data';
import { useBuyerDashboardMetrics } from './DashboardPage.data';
import type { DealView } from '@/lib/db/deals';

const DATE_FMT = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

function statusLabel(status: string): string {
  switch (status) {
    case 'new':
      return 'New';
    case 'in_progress':
      return 'In progress';
    case 'treated':
      return 'Treated';
    case 'closed':
      return 'Closed';
    default:
      return status;
  }
}

export function DashboardPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const firstName = auth.status === 'authenticated' ? auth.profile.first_name : '';
  const userId = auth.status === 'authenticated' ? auth.user.id : null;
  const metrics = useBuyerDashboardMetrics(userId);

  const [recentDeals, setRecentDeals] = useState<DealView[]>([]);
  const [recentRequests, setRecentRequests] = useState<BuyerRequestRowJoined[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    Promise.allSettled([listSavedDeals('date'), listBuyerRequests('all')]).then(
      ([savedResult, reqResult]) => {
        if (cancelled) return;

        if (savedResult.status === 'fulfilled') {
          const mapped = savedResult.value
            .slice(0, 3)
            .map(mapSavedRowToDealView)
            .filter((d): d is DealView => d !== null);
          setRecentDeals(mapped);
        }

        if (reqResult.status === 'fulfilled') {
          setRecentRequests(reqResult.value.slice(0, 3));
        }

        setLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, []);

  const hasContent = recentDeals.length > 0 || recentRequests.length > 0;

  return (
    <>
      <header className="buyer-page-head">
        <div>
          <div className="buyer-page-head__eyebrow">
            {firstName
              ? t('buyer.dashboard.greeting', { name: firstName })
              : t('buyer.dashboard.greetingFallback')}
          </div>
        </div>
      </header>

      {metrics.status === 'success' && (
        <div className="buyer-metrics">
          <Link to="/dashboard/saved" className="buyer-metric">
            <div className="buyer-metric__label">{t('buyer.dashboard.metrics.saved')}</div>
            <div className="buyer-metric__value">{metrics.metrics.saved}</div>
          </Link>
          <Link to="/dashboard/requests" className="buyer-metric">
            <div className="buyer-metric__label">{t('buyer.dashboard.metrics.active')}</div>
            <div className="buyer-metric__value">{metrics.metrics.active}</div>
          </Link>
          <Link to="/dashboard/requests" className="buyer-metric">
            <div className="buyer-metric__label">{t('buyer.dashboard.metrics.processed')}</div>
            <div className="buyer-metric__value">{metrics.metrics.processed}</div>
          </Link>
          <Link to="/dashboard/history" className="buyer-metric">
            <div className="buyer-metric__label">{t('buyer.dashboard.metrics.views')}</div>
            <div className="buyer-metric__value">{metrics.metrics.views}</div>
          </Link>
        </div>
      )}

      {loading ? (
        <div aria-busy="true">
          <div className="buyer-saved-grid" style={{ marginBottom: '2rem' }}>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
          <SkeletonText lines={4} lastWidth="40%" lineHeight={44} gap={10} />
        </div>
      ) : !hasContent ? (
        <div className="buyer-empty">
          <h2 className="buyer-empty__title">{t('buyer.dashboard.emptyTitle')}</h2>
          <p className="buyer-empty__lead">{t('buyer.dashboard.emptyLead')}</p>
          <Link to="/" className="m5-btn m5-btn--primary">
            {t('buyer.dashboard.browse')}
          </Link>
        </div>
      ) : (
        <>
          {recentDeals.length > 0 && (
            <section style={{ marginBottom: '2rem' }}>
              <div className="buyer-section__head">
                <span className="buyer-section__label">{t('buyer.dashboard.recentlySaved')}</span>
                <Link to="/dashboard/saved" className="buyer-section__cta">
                  {t('buyer.dashboard.seeAll')}
                </Link>
              </div>
              <div className="buyer-saved-grid">
                {recentDeals.map((deal) => (
                  <DealCard key={deal.id} deal={deal} />
                ))}
              </div>
            </section>
          )}

          {recentRequests.length > 0 && (
            <section>
              <div className="buyer-section__head">
                <span className="buyer-section__label">{t('buyer.dashboard.latestRequests')}</span>
                <Link to="/dashboard/requests" className="buyer-section__cta">
                  {t('buyer.dashboard.seeAll')}
                </Link>
              </div>
              <div className="admin-table-wrap">
                <table className="admin-table" style={{ fontSize: '0.875rem' }}>
                  <thead>
                    <tr>
                      <th>Deal</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRequests.map((req) => {
                      const deal = req.deals as { title?: string; reference_id?: string } | null;
                      return (
                        <tr key={req.id}>
                          <td>{deal?.title ?? deal?.reference_id ?? '—'}</td>
                          <td>
                            <span
                              style={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                color:
                                  req.status === 'new'
                                    ? 'var(--c-info, #2563eb)'
                                    : req.status === 'treated'
                                      ? 'var(--c-success, #16a34a)'
                                      : 'var(--c-muted)',
                              }}
                            >
                              {statusLabel(req.status)}
                            </span>
                          </td>
                          <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {DATE_FMT.format(new Date(req.created_at))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}
